/**
 * The business profile.
 *
 * The page the whole product exists to render. Its job is to show what is known
 * *and how it is known* — sources, scores and the fact/inference/estimate split
 * are not an appendix here, they are the reason to trust this over a search
 * engine result.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, Navigate, useParams } from 'react-router-dom'
import {
  FactGroup, ScoreBreakdownList, ScorePair, SourceList, VerificationBadge,
} from '@/components/data/trust'
import { ProductsTab, RelationshipsTab, ServicesTab } from './business/ProfileTabs'
import {
  ArrowUpRightIcon, BuildingIcon, ClockIcon, LinkIcon, MapPinIcon,
  ShieldCheckIcon, SourceIcon,
} from '@/components/ui/icons'
import {
  Alert, Badge, cx, DataRow, EmptyState, Panel, SectionHeader, Skeleton,
} from '@/components/ui/primitives'
import { businessApi } from '@/services/endpoints'
import {
  displayUrl, formatDate, formatMoney, formatRelative, humanise, joinParts, UNKNOWN,
} from '@/utils/format'
import type { AssertionType, BusinessDetail, MergedBusinessResponse } from '@/types/api'

type Tab = 'overview' | 'products' | 'services' | 'graph' | 'sources'

export function BusinessProfilePage() {
  const { slug = '' } = useParams()
  const [tab, setTab] = useState<Tab>('overview')

  const query = useQuery({
    queryKey: ['business', slug],
    queryFn: () => businessApi.detail(slug),
    enabled: Boolean(slug),
  })


  if (query.isLoading) return <ProfileSkeleton />

  if (query.isError || !query.data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <EmptyState
          icon={<BuildingIcon size={18} />}
          title="Business not found"
          description="This record does not exist, or is not published."
        />
      </div>
    )
  }

  // A merged record keeps its URL working for ever. Redirecting rather than
  // 404-ing is what makes a merge safe to perform.
  if ('merged' in query.data) {
    const merged = query.data as MergedBusinessResponse
    return <Navigate to={`/businesses/${merged.redirect_to}`} replace />
  }

  const business = query.data as BusinessDetail
  const factCount = (['fact', 'inference', 'estimate'] as AssertionType[]).reduce(
    (total, kind) => total + (business.facts[kind]?.length ?? 0),
    0,
  )

  const tabs: Array<{ id: Tab; label: string; count?: number }> = [
    { id: 'overview', label: 'Overview' },
    { id: 'products', label: 'Products & prices', count: business.counts.products },
    { id: 'services', label: 'Services', count: business.counts.services },
    { id: 'graph', label: 'Relationships', count: business.counts.relationships },
    { id: 'sources', label: 'Sources', count: business.sources.length },
  ]

  return (
    <div>
      {/* -- Header --------------------------------------------------------- */}
      <header className="border-b border-[var(--border-subtle)] bg-[var(--surface-panel)]">
        <div className="mx-auto max-w-7xl px-4 py-7 sm:px-6">
          <nav className="mb-3 flex items-center gap-1.5 text-xs text-[var(--text-tertiary)]">
            <Link to="/search" className="hover:text-[var(--accent-text)]">Businesses</Link>
            {business.location_detail.state_slug ? (
              <>
                <span>/</span>
                <Link
                  to={`/search?state=${business.location_detail.state_slug}`}
                  className="hover:text-[var(--accent-text)]"
                >
                  {business.location_detail.state}
                </Link>
              </>
            ) : null}
          </nav>

          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-2xl font-semibold tracking-tight text-[var(--text-primary)]">
                  {business.name}
                </h1>
                <VerificationBadge status={business.verification_status} />
                {business.is_claimed ? <Badge tone="accent">Claimed by owner</Badge> : null}
              </div>

              {business.legal_name && business.legal_name !== business.name ? (
                <p className="mt-1 text-sm text-[var(--text-tertiary)]">
                  Registered as {business.legal_name}
                </p>
              ) : null}

              {business.catalogue_summary || business.short_description ? (
                <p className="mt-2.5 max-w-2xl text-sm leading-relaxed text-[var(--text-secondary)]">
                  {business.catalogue_summary || business.short_description}
                </p>
              ) : null}

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-[var(--text-secondary)]">
                <span className="inline-flex items-center gap-1.5">
                  <BuildingIcon size={13} />
                  {humanise(business.business_type)}
                </span>
                {business.location_detail.state ? (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPinIcon size={13} />
                    {joinParts(
                      business.location_detail.city,
                      business.location_detail.local_government,
                      business.location_detail.state,
                    )}
                  </span>
                ) : null}
                {business.website_url ? (
                  <a
                    href={business.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[var(--accent-text)] hover:underline"
                  >
                    <LinkIcon size={13} />
                    {displayUrl(business.website_url, 34)}
                    <ArrowUpRightIcon size={11} />
                  </a>
                ) : null}
                <span className="inline-flex items-center gap-1.5">
                  <ClockIcon size={13} />
                  {business.last_verified_at
                    ? `Checked ${formatRelative(business.last_verified_at)}`
                    : 'Never verified'}
                </span>
              </div>

              {business.categories.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {business.categories.map((category) =>
                    category.slug ? (
                      <Link
                        key={category.slug}
                        to={`/search?category=${category.slug}`}
                        className="rounded-full border border-[var(--border-subtle)] px-2.5 py-0.5 text-xs text-[var(--text-secondary)] hover:border-[var(--accent-border)] hover:text-[var(--accent-text)]"
                      >
                        {category.name}
                      </Link>
                    ) : null,
                  )}
                </div>
              ) : null}
            </div>

            <Panel className="w-full shrink-0 lg:w-80">
              <ScorePair
                stacked
                quality={business.quality_breakdown}
                trust={business.trust_breakdown}
              />
              <p className="mt-3 border-t border-[var(--border-subtle)] pt-3 text-2xs leading-relaxed text-[var(--text-tertiary)]">
                {/* Stating the distinction in words, every time. A reader who
                    conflates the two scores draws exactly the wrong conclusion. */}
                A complete profile is not the same as a confirmed one. Quality measures how
                much we hold; corroboration measures how much of it is independently
                supported.
              </p>
            </Panel>
          </div>
        </div>
      </header>

      {/* -- Tabs ----------------------------------------------------------- */}
      <div className="sticky top-14 z-30 border-b border-[var(--border-subtle)] bg-[var(--surface-page)]/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 no-scrollbar sm:px-6">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={cx(
                'relative shrink-0 px-3 py-3 text-sm transition-colors',
                tab === item.id
                  ? 'font-medium text-[var(--text-primary)]'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
              )}
            >
              {item.label}
              {item.count !== undefined && item.count > 0 ? (
                <span className="ml-1.5 text-2xs tabular text-[var(--text-tertiary)]">
                  {item.count}
                </span>
              ) : null}
              {tab === item.id ? (
                <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-[var(--accent)]" />
              ) : null}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-7 sm:px-6">
        {tab === 'overview' ? <Overview business={business} factCount={factCount} onShowProducts={() => setTab('products')} /> : null}
        {tab === 'products' ? <ProductsTab slug={slug} /> : null}
        {tab === 'services' ? <ServicesTab slug={slug} /> : null}
        {tab === 'graph' ? <RelationshipsTab slug={slug} /> : null}
        {tab === 'sources' ? (
          <Panel>
            <SectionHeader
              title="Where this record comes from"
              description="Every claim on this profile traces back to one of these."
            />
            <div className="mt-3">
              <SourceList sources={business.sources} />
            </div>
          </Panel>
        ) : null}
      </div>
    </div>
  )
}

/* -- Overview -------------------------------------------------------------- */
function Overview({ business, factCount, onShowProducts }: {
  business: BusinessDetail
  factCount: number
  onShowProducts: () => void
}) {
  // Same key as the Products tab, so opening the tab is a cache hit rather
  // than a second fetch. On the overview because the same URL is also
  // server-rendered, and that page leads with the price table — a reader who
  // refreshed saw more than the one who clicked, which reads as a bug
  // because it is one.
  const offers = useQuery({
    queryKey: ['business', business.slug, 'prices'],
    queryFn: () => businessApi.prices(business.slug, 1),
  })

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_20rem]">
      <div className="space-y-6">
        {/* The index's own account of the company, written from its records
            and checked against them before publication. It leads because it is
            the part a reader cannot get from the source site. */}
        {business.profile ? (
          <Panel>
            <SectionHeader
              title={business.profile.title}
              description={`Written from ${business.profile.source_count} published source${
                business.profile.source_count === 1 ? '' : 's'
              } held by this index, and checked against them before publication.`}
            />
            <div className="mt-4 space-y-5">
              {business.profile.sections.map((section) => (
                <section key={section.heading}>
                  {section.heading ? (
                    <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                      {section.heading}
                    </h3>
                  ) : null}
                  {section.paragraphs.map((paragraph, index) => (
                    <p
                      key={index}
                      className="mt-2 max-w-[38rem] text-sm leading-relaxed text-[var(--text-secondary)]"
                    >
                      {paragraph}
                    </p>
                  ))}
                </section>
              ))}
            </div>
            {/* A real navigation, not a router link. Report pages are
                server-rendered by Flask so they are indexable; the SPA has no
                route for them, and a client-side link would land on "Page not
                found" until the reader refreshed. */}
            <a
              href={`/reports/${business.profile.slug}`}
              className="mt-4 inline-block text-xs text-[var(--accent-text)] hover:underline"
            >
              Read this as a standalone report
            </a>
          </Panel>
        ) : null}

        {/* The external half of the record. A catalogue entry answers two
            questions — what a company does, which comes off its own site, and
            where it appears in the wider world, which does not. `nofollow`
            because these are links this index recorded, not ones it vouches
            for. */}
        {business.social_profiles?.length ? (
          <Panel>
            <SectionHeader
              title="Elsewhere on the web"
              description={`Linked from ${business.name}'s own website. This index records where the business publishes; it does not read those pages.`}
            />
            <ul className="mt-4 flex flex-wrap gap-2">
              {business.social_profiles.map((entry) => (
                <li key={entry.url}>
                  <a
                    href={entry.url}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--border)] bg-[var(--surface-sunken)] px-3 py-1.5 text-sm text-[var(--text-secondary)] transition-colors hover:border-[var(--accent-border)] hover:text-[var(--text-primary)]"
                  >
                    {entry.label || entry.platform}
                    <ArrowUpRightIcon className="h-3 w-3" />
                  </a>
                </li>
              ))}
            </ul>
          </Panel>
        ) : null}

        {/* Products and prices, on the overview — parity with the
            server-rendered page, which leads with this table. The tab remains
            the full view; this shows the first rows so nothing a refresh
            reveals is missing from the clicked view. */}
        {(offers.data?.data?.length ?? 0) > 0 ? (
          <Panel padded={false}>
            <div className="p-5 pb-3">
              <SectionHeader
                title={`Products and prices from ${business.name}`}
              />
            </div>
            <div className="overflow-x-auto px-5 pb-2">
              <table className="w-full min-w-[32rem] text-sm">
                <thead>
                  <tr className="border-b border-[var(--border-default)] text-left">
                    <th className="pb-2 field-label font-semibold">Product</th>
                    <th className="pb-2 field-label font-semibold text-right">Price</th>
                    <th className="pb-2 field-label font-semibold text-right">Observed</th>
                  </tr>
                </thead>
                <tbody className="divide-hairline">
                  {offers.data!.data.slice(0, 8).map((offer, index) => (
                    <tr key={index}>
                      <td className="max-w-[18rem] truncate py-2 pr-3">
                        {offer.product ? (
                          <Link
                            to={`/products/general/${offer.product.slug}`}
                            className="text-[var(--text-primary)] hover:text-[var(--accent-text)]"
                          >
                            {offer.product.name}
                          </Link>
                        ) : (
                          <span className="text-[var(--text-secondary)]">Unnamed product</span>
                        )}
                      </td>
                      <td className="py-2 text-right font-medium tabular text-[var(--text-primary)]">
                        {offer.price_type === 'quote' || offer.amount == null
                          ? 'On request'
                          : formatMoney(offer.amount, offer.currency ?? 'NGN')}
                      </td>
                      <td className="py-2 text-right text-xs text-[var(--text-tertiary)]">
                        {offer.observed_at ? formatRelative(offer.observed_at) : UNKNOWN}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(offers.data!.data.length > 8 || Number(offers.data!.meta?.total ?? 0) > 8) ? (
              <button
                type="button"
                onClick={onShowProducts}
                className="block w-full border-t border-[var(--border-subtle)] px-5 py-2.5 text-left text-xs text-[var(--accent-text)] hover:underline"
              >
                All products and prices
              </button>
            ) : null}
          </Panel>
        ) : null}

        {/* The company's own copy, kept as evidence of what they claim and
            attributed to them. Presenting a slogan under "About" reads as the
            catalogue endorsing it; the index's own account is above. */}
        {business.description &&
        business.description !== business.catalogue_summary ? (
          <Panel>
            <SectionHeader
              title="In their own words"
              description={`How ${business.name} describes itself. Not a description or an endorsement by this index.`}
            />
            <blockquote className="mt-3 border-l-2 border-[var(--border-default)] pl-3 text-sm italic leading-relaxed text-[var(--text-secondary)]">
              {business.description}
            </blockquote>
          </Panel>
        ) : null}

        {/* Facts, inferences and estimates — spec §54. Three groups, three
            visual treatments, never merged into one paragraph. */}
        {factCount > 0 ? (
          <Panel>
            <SectionHeader
              title="What we hold on this business"
              description="Separated by how much each claim can be relied on."
            />
            <div className="mt-4 space-y-5">
              {(['fact', 'inference', 'estimate'] as AssertionType[]).map((kind) => (
                <FactGroup key={kind} type={kind} facts={business.facts[kind] ?? []} />
              ))}
            </div>
          </Panel>
        ) : null}

        <Panel>
          <SectionHeader
            title="How the corroboration score is made up"
            description="Every component, and whether it is satisfied."
          />
          <div className="mt-2">
            <ScoreBreakdownList breakdown={business.trust_breakdown} />
          </div>
        </Panel>

        {business.quality_breakdown.missing && business.quality_breakdown.missing.length > 0 ? (
          <Alert tone="info" title="Not yet recorded">
            <p className="text-sm">
              {business.quality_breakdown.missing.join(', ')}.
              {' '}
              <Link to="/methodology#corrections" className="underline">
                Send a correction
              </Link>{' '}
              if you can supply any of these.
            </p>
          </Alert>
        ) : null}
      </div>

      <aside className="space-y-6">
        <Panel>
          <SectionHeader title="Contact" />
          <dl className="mt-2 divide-hairline">
            <DataRow
              label="Website"
              value={
                business.website_url ? (
                  <a
                    href={business.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--accent-text)] hover:underline"
                  >
                    {displayUrl(business.website_url, 26)}
                  </a>
                ) : (
                  UNKNOWN
                )
              }
            />
            <DataRow
              label="Phone"
              value={
                business.phone ? (
                  <a href={`tel:${business.phone}`} className="hover:text-[var(--accent-text)]">
                    {business.phone}
                  </a>
                ) : (
                  UNKNOWN
                )
              }
            />
            <DataRow
              label="Email"
              value={
                business.email ? (
                  <a href={`mailto:${business.email}`} className="hover:text-[var(--accent-text)]">
                    {business.email}
                  </a>
                ) : (
                  UNKNOWN
                )
              }
            />
            <DataRow label="Address" value={business.address ?? UNKNOWN} />
          </dl>
        </Panel>

        <Panel>
          <SectionHeader title="Details" />
          <dl className="mt-2 divide-hairline">
            <DataRow label="Type" value={humanise(business.business_type)} />
            <DataRow label="Ownership" value={humanise(business.ownership_type)} />
            <DataRow label="Founded" value={business.year_founded ?? UNKNOWN} />
            <DataRow label="Staff" value={business.employee_range ?? UNKNOWN} />
            <DataRow label="Sources" value={business.source_count} />
            <DataRow label="First indexed" value={formatDate(business.created_at)} />
          </dl>
        </Panel>

        {business.verification_signals.length > 0 ? (
          <Panel>
            <SectionHeader
              title="Verification"
              description="What has actually been checked."
            />
            <ul className="mt-2 divide-hairline">
              {business.verification_signals.map((signal) => (
                <li key={signal.type} className="flex items-center justify-between gap-3 py-2">
                  <span className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                    <ShieldCheckIcon
                      size={13}
                      className={
                        signal.is_current
                          ? 'text-[var(--color-verified)]'
                          : 'text-[var(--text-tertiary)]'
                      }
                    />
                    {humanise(signal.type)}
                  </span>
                  <span className="text-2xs text-[var(--text-tertiary)]">
                    {signal.is_current ? formatDate(signal.verified_at) : humanise(signal.status)}
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        ) : null}

        <Panel className="bg-[var(--surface-sunken)]">
          <div className="flex gap-2.5">
            <SourceIcon size={16} className="mt-0.5 shrink-0 text-[var(--text-tertiary)]" />
            <p className="text-2xs leading-relaxed text-[var(--text-secondary)]">
              This is an index entry compiled from published sources, not an endorsement.
              Confirm details directly with the business before transacting.
            </p>
          </div>
        </Panel>
      </aside>
    </div>
  )
}


/* -- Loading --------------------------------------------------------------- */
function ProfileSkeleton() {
  return (
    <div>
      <div className="border-b border-[var(--border-subtle)] bg-[var(--surface-panel)]">
        <div className="mx-auto max-w-7xl px-4 py-7 sm:px-6">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="mt-4 h-8 w-80" />
          <Skeleton className="mt-3 h-4 w-full max-w-xl" />
          <Skeleton className="mt-2 h-4 w-64" />
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 py-7 sm:px-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_20rem]">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    </div>
  )
}
